import { useState } from "react";
import {
  JhTableBody,
  JhTableCell,
  JhTableHead,
  JhTableHeader,
  JhTableRow
} from "designPackages/tablePackage";
import { JhText } from "designPackages/textPackage";
import { JhButton } from "designPackages/buttonPackage";
import BaseTable from "./BaseTable";
import TransactionHistoryV2TableHeader from "../TransactionHistoryV2TableHeader";
import { TransactionStatusBadge } from "../utils/statusBadgeUtils";
import { PENDING, transactionLabelMap, transactionKeyLabelMapDesktop } from "services/constants";
import Image from "next/image";
import { Download } from "lucide-react";
import { JhUseToast } from "designPackages/toastPackage";
import useModalStore from "zustandStores/useModalStore";
import { getInvoiceEmail } from "utils/invoiceUtils";

interface TransactionData {
  transactionLabel: string;
  lastUpdated: string;
  status: string;
  credits: string;
  expirationDate: string;
  transactionDate: string;
  transactionTime: string;
  closingBalance: string;
  activity: string;
  isInvoiceAvailable?: boolean;
  invoiceEmail?: string;
  orderId?: string;
  [key: string]: any;
}

interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

interface TransactionHistoryTableProps {
  transactionData: TransactionData[];
  onFilterSelect?: (filter: string) => void;
  isLoading?: boolean;
  filterOptions?: FilterOption[];
  selectedTab?: string;
}

const TableEmptyState = () => (
  <div id="th-v2-history-table-empty-state" className="flex flex-col items-center justify-center py-16 gap-2.5">
    <Image
      src="/hire/transactionHistoryV2/no_transaction.png"
      alt="no transactions"
      width={100}
      height={100}
    />
    <JhText variant="emphasis3" color="foreground">
      No transactions made yet
    </JhText>
  </div>
);

const TransactionHistoryTable = ({
  transactionData = [],
  onFilterSelect,
  isLoading = false,
  filterOptions = [],
  selectedTab = "ALL"
}: TransactionHistoryTableProps) => {
  const isEmpty = !transactionData.length && !isLoading;

  const { toast } = JhUseToast();
  const { setModalData } = useModalStore(state => ({
    setModalData: state.setModalData
  }));
  const [loadingInvoiceId, setLoadingInvoiceId] = useState<string | null>(null);

  const handleInvoiceClick = async ({ orderId, invoiceEmail }: { orderId: string; invoiceEmail: string }) => {
    await getInvoiceEmail({
      orderId,
      invoiceEmail,
      toast,
      setLoadingInvoiceId,
      setModalData
    });
  };

  return (
    <BaseTable isLoading={isLoading}>
      <JhTableHeader>
        <JhTableRow>
          <JhTableHead className="w-[25%]">
            <TransactionHistoryV2TableHeader
              onFilterSelect={onFilterSelect}
              filterOptions={filterOptions}
              selectedTab={selectedTab}
            />
          </JhTableHead>
          <JhTableHead className="w-[20%]">
            <JhText variant="subtitle4" color="muted-foreground">
              Credits
            </JhText>
          </JhTableHead>
          <JhTableHead className="w-[25%]">
            <JhText variant="subtitle4" color="muted-foreground">
              Status
            </JhText>
          </JhTableHead>
          <JhTableHead className="w-[15%]">
            <JhText variant="subtitle4" color="muted-foreground">
              Date
            </JhText>
          </JhTableHead>
          <JhTableHead className="w-[15%] text-left">
            <JhText variant="subtitle4" color="muted-foreground">
              Closing Balance
            </JhText>
          </JhTableHead>
          <JhTableHead className="w-[15%] text-left">
            <JhText variant="subtitle4" color="muted-foreground">
              Invoice
            </JhText>
          </JhTableHead>
        </JhTableRow>
      </JhTableHeader>
      <JhTableBody>
        {isEmpty ? (
          <JhTableRow>
            <JhTableCell colSpan={6} className="text-center">
              <TableEmptyState />
            </JhTableCell>
          </JhTableRow>
        ) : (
          transactionData.map((row, index) => {
            const {
              transactionLabel = "",
              status = "",
              credits = "",
              expirationDate = "",
              transactionDate = "",
              transactionTime = "",
              closingBalance = "",
              orderId = "",
              lastUpdated = "",
              activity = "",
              isInvoiceAvailable = false,
              invoiceEmail = ""
            } = row;

            // Get the keys to display based on transaction label
            const labelValues = transactionLabelMap[transactionLabel] || [];

            // Filter row data to get only the keys that are in labelValues and have values
            const transactionSubLabel = Object.fromEntries(
              Object.entries(row).filter(
                ([key, value]) => labelValues.includes(key) && value != null
              )
            );

            // Format date
            const formattedDate = transactionDate
              ? new Date(transactionDate).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric"
              })
              : "";

            // Format time from transactionTime field
            const formattedTime = transactionTime || "";

            // Format credits with + or - sign
            const formatCredits = (credits: string) => {
              if (!credits) return "";
              return credits;
            };

            return (
              <JhTableRow key={index}>
                <JhTableCell>
                  <div id={`th-v2-history-table-transaction-label-${index}`} className="space-y-1">
                    <JhText variant="subtitle4" color="foreground">
                      {transactionLabel}
                    </JhText>
                    {Object.entries(transactionSubLabel).map(([key, value]) => {
                      const labelKey = key === "jobId" && activity === "JOB_BOOST"
                        ? "boostedJobId"
                        : key;
                      const label = transactionKeyLabelMapDesktop[labelKey] || "";

                      return (
                        <JhText key={key} variant="caption2" color="muted-foreground">
                          {label}{value}
                        </JhText>
                      );
                    })}
                  </div>
                </JhTableCell>
                <JhTableCell>
                  <div id={`th-v2-history-table-credits-${index}`} className="space-y-1">
                    <JhText variant="subtitle3" color="foreground">
                      {formatCredits(credits)}
                    </JhText>
                    {expirationDate && (
                      <JhText variant="caption2" color="muted-foreground">
                        {expirationDate}
                      </JhText>
                    )}
                  </div>
                </JhTableCell>
                <JhTableCell>
                  <div id={`th-v2-history-table-status-${index}`} className="space-y-1">
                    <TransactionStatusBadge status={status} />
                    {lastUpdated && (
                      <JhText variant="caption2" color="muted-foreground">
                        {lastUpdated}
                      </JhText>
                    )}
                  </div>
                </JhTableCell>
                <JhTableCell>
                  <div id={`th-v2-history-table-date-${index}`} className="space-y-1">
                    <JhText variant="subtitle4" color="foreground">
                      {formattedDate}
                    </JhText>
                    {formattedTime && (
                      <JhText variant="caption2" color="muted-foreground">
                        {formattedTime}
                      </JhText>
                    )}
                  </div>
                </JhTableCell>
                <JhTableCell className="text-left">
                  <JhText variant="subtitle4" color="foreground">
                    {status === PENDING ? "-" : closingBalance}
                  </JhText>
                </JhTableCell>
                <JhTableCell className="text-left">
                  {isInvoiceAvailable && orderId ? (
                    <JhButton
                      id={`th-v2-get-invoice-btn-${index}`}
                      variant="outline"
                      size="sm"
                      disabled={loadingInvoiceId === orderId}
                      onClick={() => handleInvoiceClick({ orderId, invoiceEmail })}
                      className="flex items-center gap-1.5"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Get Invoice
                    </JhButton>
                  ) : (
                    <JhText variant="subtitle4" color="muted-foreground">
                      --
                    </JhText>
                  )}
                </JhTableCell>
              </JhTableRow>
            );
          })
        )}
      </JhTableBody>
    </BaseTable>
  );
};

export default TransactionHistoryTable;
